import { Injectable } from '@angular/core';
import {
  NavatticDemoDialogComponent,
  NavatticDemoDialogData,
} from '@app/+onboarding/components/demo-dialog/navattic-demo-dialog.component';
import { ApiGateway } from '@app/api/ApiGateway.service';
import { ClockLocationData } from '@app/reducers/orm/clock-location/clock-location.model';
import { TranslateService } from '@ngx-translate/core';
import { of, switchMap, take, tap } from 'rxjs';

import { SbDialogService } from '../../../../../libs/ui/src';
import { LocaleLanguage } from '../../../+authenticated/shared/locale/locale.helper';
import { OnboardingDialogResult } from '../../../+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { UserGuideType } from '../../../reducers/orm/user-guide';
import { SmbHospitalityOnboardingChecklistStep } from '../../../shared/onboarding/onboarding-checklist.model';
import { OnboardingChecklistService } from '../../../shared/onboarding/onboarding-checklist.service';
import { OnboardingUtils } from '../../onboarding.utils';
import { AutoApproveTimesheetHoursOption, TimeTrackingMethod } from '../chapter.model';
import { TimeTrackingDialogComponent } from '../time-tracking/time-tracking-dialog.component';
import { TimeTrackingSummaryDialogComponent } from '../time-tracking/time-tracking-summary-dialog.component';

export interface TimeTrackingSettingsPayload {
  timeTrackingMethod: TimeTrackingMethod;
  autoApproveSchedule: boolean;
  autoApproveClockedTime: boolean;
  clockLocation?: ClockLocationData;
}

export interface TimeTrackingSettings
  extends Omit<TimeTrackingSettingsPayload, 'autoApproveSchedule' | 'autoApproveClockedTime'> {
  autoApproveSchedule: AutoApproveTimesheetHoursOption;
  autoApproveClockedTime: AutoApproveTimesheetHoursOption;
}

@Injectable({
  providedIn: 'root',
})
export class TimeTrackingOnboardingService {
  public constructor(
    private readonly translate: TranslateService,
    private readonly onboardingChecklist: OnboardingChecklistService,
    private readonly onboardingUtils: OnboardingUtils,
    private readonly dialogService: SbDialogService,
    private readonly api: ApiGateway,
  ) {}

  public openDialog(originator = 'onboarding') {
    return this.dialogService
      .setSize('xxl')
      .open(TimeTrackingDialogComponent, { data: { originator } })
      .closed.pipe(
        take(1),
        tap((result: OnboardingDialogResult) => {
          if (!result?.action) {
            this.onboardingUtils.stripOnboardingParamsFromRoute();
          }
        }),
      );
  }

  public openDemo(skipFlow: boolean) {
    const langDemoMap = {
      [LocaleLanguage.ENGLISH]: 'cm41cilln000103jsfjiferry',
      [LocaleLanguage.DUTCH]: 'cm5gqwfp7000403kz9vf337xi',
      [LocaleLanguage.GERMAN]: 'cm5gv0cu1000003mb4use1fx6',
    };

    const demoId = langDemoMap[this.translate.currentLang] || langDemoMap['en-GB'];

    return this.dialogService
      .setSize('fullscreen')
      .open<NavatticDemoDialogComponent, NavatticDemoDialogData>(NavatticDemoDialogComponent, {
        data: { demoId },
      })
      .closed.pipe(
        switchMap(() => {
          if (!skipFlow) {
            return this.onboardingChecklist.completeStep(
              UserGuideType.SELF_ONBOARDING_CHECKLIST,
              SmbHospitalityOnboardingChecklistStep.TIME_TRACKING,
            );
          }
          return of(false);
        }),
      );
  }

  public openSummary(originator: string) {
    return this.dialogService
      .setSize('xxl')
      .open(TimeTrackingSummaryDialogComponent, { data: { originator } })
      .closed.pipe(take(1));
  }

  public savePreferences(settings: TimeTrackingSettings) {
    return this.api.post('onboarding/time_tracking', this.getTimeTrackingPayload(settings));
  }

  private getTimeTrackingPayload(settings: TimeTrackingSettings): TimeTrackingSettingsPayload {
    return {
      ...settings,
      autoApproveSchedule: settings.autoApproveSchedule === AutoApproveTimesheetHoursOption.YES,
      autoApproveClockedTime: settings.autoApproveClockedTime === AutoApproveTimesheetHoursOption.YES,
    };
  }
}
